/**
 * TREATX-BATCH-B1.2 — Canonical money & currency presentation helpers.
 *
 * SINGLE FORMATTER AUTHORITY for the patient web UI. Rules:
 *
 * 1. Backend remains the SOLE arithmetic authority (canonical
 *    `financial_summary`). The frontend never reconstructs Total,
 *    Base, Deposit, Balance or per-installment.
 * 2. This module ONLY formats the numbers the backend already produced.
 *    Storage precision is never mutated.
 * 3. Amounts that are legitimately decimals (Deposit, Balance,
 *    per-installment) render with two fractional digits so users
 *    never see a bare `.4` where `.40` is meant.
 * 4. Integer-valued amounts (Base, Additional Total, Total) render
 *    without trailing zeros to preserve the established visual
 *    style — thousand separators only.
 * 5. Currency symbol resolution mirrors the existing `sym()` shape
 *    used across the app so no consumer needs a second helper.
 *
 * Do NOT add a second money formatter anywhere. Do NOT hard-code
 * `toLocaleString({...})` in components — call these instead.
 */

const CURRENCY_SYMBOLS = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  TRY: '₺',
  AED: 'AED ',
};

export const currencySymbol = (currency) => CURRENCY_SYMBOLS[currency] || `${currency} `;

/**
 * Format a raw numeric amount. Presentation-only.
 *
 * @param {number|null|undefined} amount
 * @param {object} [opts]
 * @param {number} [opts.minimumFractionDigits=0]
 * @param {number} [opts.maximumFractionDigits=2]
 * @returns {string}
 */
export const formatNumber = (amount, opts = {}) => {
  if (amount == null || !Number.isFinite(Number(amount))) return '—';
  const { minimumFractionDigits = 0, maximumFractionDigits = 2 } = opts;
  return Number(amount).toLocaleString(undefined, {
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

/**
 * Full money string with currency symbol.
 *
 * @param {number|null|undefined} amount
 * @param {string} [currency='EUR']
 * @param {object} [opts] — same as formatNumber
 */
export const formatMoney = (amount, currency = 'EUR', opts) => {
  if (amount == null || !Number.isFinite(Number(amount))) return '—';
  return `${currencySymbol(currency)}${formatNumber(amount, opts)}`;
};

/**
 * Amounts that are legitimately fractional (Deposit, Balance,
 * per-installment) — always two decimals. Base / Additional / Total
 * usually round evenly so they should use `formatMoney` (no forced
 * decimals), which is consistent with the PDF's visual style.
 */
export const formatMoneyPrecise = (amount, currency = 'EUR') =>
  formatMoney(amount, currency, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
